import { useState, useEffect } from 'react';
import { X, Loader2, Search, Check, AlertCircle, Building2, Wheat, Package, Box, Fuel, Wrench, Laptop, Megaphone, Zap, MoreHorizontal } from 'lucide-react';

const SECTION_COLOR = '#3B82F6';

const IKONA_KATEGORIE = {
    Wheat, Package, Box, Fuel, Wrench, Laptop, Megaphone, Zap, MoreHorizontal,
};

/**
 * Modal pro založení dodavatele z ARES.
 *
 * Tok:
 *  1) Uživatel zadá IČO (předplněno z props.defaultIco pokud AI scan něco zachytil)
 *  2) Klik na "Dohledat v ARES" → GET /dodavatele/ares-lookup
 *  3) Modal zobrazí preview (nazev, DIČ, adresa, navrhované kategorie)
 *  4) Uživatel může upravit zaškrtnuté kategorie
 *  5) Klik na "Založit a spárovat" → POST /dodavatele/from-ares
 *  6) onSuccess(dodavatel) — parent refetchne detail dokladu
 */
export default function ZalozitDodavateleModal({
    open,
    onClose,
    onSuccess,
    dokladId = null,        // pokud zadán, nový dodavatel se spáruje s dokladem
    defaultIco = '',         // pokud AI scan zachytil IČO, předvyplnit
    defaultNazev = '',       // naskenovaný název dodavatele — předvyplní ruční režim
    cardStyle,
    isDark,
}) {
    const [ico, setIco] = useState(defaultIco);
    const [kategorieDostupne, setKategorieDostupne] = useState([]);
    const [aresPreview, setAresPreview] = useState(null);
    const [selectedKategorie, setSelectedKategorie] = useState([]);
    const [hledani, setHledani] = useState(false);
    const [zakladani, setZakladani] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [rezim, setRezim] = useState('existujici'); // 'existujici' | 'ares' | 'rucni'
    const [dodavatele, setDodavatele] = useState([]);
    const [q, setQ] = useState('');
    const [prirazovani, setPrirazovani] = useState(null); // id přiřazovaného
    // Ruční / zahraniční dodavatel (ARES nedohledá — Anthropic apod.)
    const emptyForm = { nazev: '', dic: '', adresa: '', iban: '', cislo_uctu: '', je_platce_dph: false };
    const [form, setForm] = useState(emptyForm);
    const setF = (k, val) => setForm(prev => ({ ...prev, [k]: val }));

    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    // Reset state při otevření
    useEffect(() => {
        if (open) {
            setIco(defaultIco || '');
            setAresPreview(null);
            setSelectedKategorie([]);
            setChyba(null);
            setRezim('existujici');
            setQ('');
            setForm({ ...emptyForm, nazev: defaultNazev || '' });
        }
    }, [open, defaultIco, defaultNazev]);

    // Načíst existující dodavatele pro vyhledání
    useEffect(() => {
        if (!open || dodavatele.length > 0) return;
        fetch('/api/dodavatele')
            .then(r => r.json())
            .then(d => setDodavatele(Array.isArray(d) ? d : (d?.data || d?.dodavatele || [])))
            .catch(() => {});
    }, [open, dodavatele.length]);

    const handlePriradit = async (dod) => {
        if (!dokladId) { onSuccess?.(dod); onClose(); return; }
        setPrirazovani(dod.id);
        setChyba(null);
        try {
            const r = await fetch(`/api/finance/doklady/${dokladId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ dodavatel_id: dod.id }),
            });
            if (!r.ok) { const e = await r.json().catch(() => ({})); setChyba(e.message || 'Přiřazení selhalo'); }
            else { onSuccess?.(dod); onClose(); }
        } catch { setChyba('Síťová chyba'); }
        setPrirazovani(null);
    };

    // Načíst číselník kategorií
    useEffect(() => {
        if (!open || kategorieDostupne.length > 0) return;
        fetch('/api/dodavatele/kategorie')
            .then(r => r.json())
            .then(setKategorieDostupne)
            .catch(() => {});
    }, [open, kategorieDostupne.length]);

    const handleAresLookup = async () => {
        const icoClean = ico.replace(/\D/g, '');
        if (icoClean.length !== 8) {
            setChyba('IČO musí mít 8 číslic');
            return;
        }
        setHledani(true);
        setChyba(null);
        try {
            const r = await fetch(`/api/dodavatele/ares-lookup?ico=${icoClean}`);
            const data = await r.json();
            if (!r.ok) {
                setChyba(data.error || 'ARES nedostupný');
                setAresPreview(null);
            } else {
                setAresPreview(data);
                setSelectedKategorie(data.navrhovane_kategorie || []);
            }
        } catch (e) {
            setChyba('Síťová chyba');
        }
        setHledani(false);
    };

    const handleZalozit = async () => {
        if (!aresPreview) return;
        setZakladani(true);
        setChyba(null);
        try {
            const r = await fetch('/api/dodavatele/from-ares', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({
                    ico: aresPreview.ico,
                    doklad_id: dokladId,
                    kategorie: selectedKategorie,
                }),
            });
            const data = await r.json();
            if (!r.ok) {
                setChyba(data.error || 'Založení selhalo');
            } else {
                onSuccess?.(data.dodavatel);
                onClose();
            }
        } catch (e) {
            setChyba('Síťová chyba');
        }
        setZakladani(false);
    };

    // Ruční / zahraniční dodavatel (bez ARES). Založí kartu a rovnou spáruje
    // s dokladem (což naučí alias → příští scan spáruje sám).
    const handleZalozitRucni = async () => {
        if (!form.nazev.trim()) { setChyba('Zadej název dodavatele'); return; }
        setZakladani(true);
        setChyba(null);
        try {
            const r = await fetch('/api/dodavatele', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ ...form, nazev: form.nazev.trim(), kategorie: selectedKategorie }),
            });
            const dod = await r.json();
            if (!r.ok) { setChyba(dod.message || dod.error || 'Založení selhalo'); }
            else { await handlePriradit(dod); }   // spáruje s dokladem + naučí alias
        } catch { setChyba('Síťová chyba'); }
        setZakladani(false);
    };

    const toggleKategorie = (kod) => {
        setSelectedKategorie(prev =>
            prev.includes(kod) ? prev.filter(k => k !== kod) : [...prev, kod]
        );
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}
        >
            <div
                className="rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
                style={cardStyle}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <div className="flex items-center gap-2">
                        <Building2 size={18} style={{ color: SECTION_COLOR }} />
                        <h2 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                            Dodavatel
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:opacity-70">
                        <X size={16} style={{ color: 'var(--text-tertiary)' }} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 space-y-4">
                    {/* Přepínač: existující vs nový (ARES) */}
                    {dokladId && (
                        <div className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-default)' }}>
                            {[['existujici', 'Existující'], ['ares', 'Nový (ARES)'], ['rucni', 'Ruční / zahraniční']].map(([k, l]) => (
                                <button key={k} onClick={() => setRezim(k)} className="px-3 py-1.5 text-xs font-semibold"
                                    style={{ background: rezim === k ? SECTION_COLOR : 'transparent', color: rezim === k ? '#fff' : 'var(--text-secondary)' }}>{l}</button>
                            ))}
                        </div>
                    )}

                    {/* Vyhledání existujícího dodavatele z katalogu */}
                    {rezim === 'existujici' && (
                        <div>
                            <div className="relative mb-2">
                                <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-tertiary)' }} />
                                <input value={q} onChange={e => setQ(e.target.value)} autoFocus
                                    placeholder="Hledat dodavatele (název / IČO)…"
                                    className="w-full rounded-lg pl-8 pr-3 py-2 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                            </div>
                            <div className="space-y-1 max-h-72 overflow-y-auto">
                                {dodavatele
                                    .filter(d => { const s = q.trim().toLowerCase(); return !s || (d.nazev || '').toLowerCase().includes(s) || String(d.ico || '').includes(s); })
                                    .slice(0, 50)
                                    .map(d => (
                                        <button key={d.id} onClick={() => handlePriradit(d)} disabled={prirazovani === d.id}
                                            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left"
                                            style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-raised)', border: '1px solid var(--border-default)' }}>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{d.nazev}</div>
                                                <div className="text-[11px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                                                    {d.ico ? `IČO ${d.ico}` : ''}{d.dic ? ` · ${d.dic}` : ''}
                                                </div>
                                            </div>
                                            {prirazovani === d.id
                                                ? <Loader2 size={14} className="animate-spin" style={{ color: SECTION_COLOR }} />
                                                : <Check size={14} style={{ color: SECTION_COLOR, opacity: 0.6 }} />}
                                        </button>
                                    ))}
                                {dodavatele.length === 0 && (
                                    <div className="text-xs text-center py-4" style={{ color: 'var(--text-tertiary)' }}>Načítám dodavatele…</div>
                                )}
                            </div>
                            <div className="text-[11px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
                                Nenašel jsi ho? Přepni na <button onClick={() => setRezim('ares')} style={{ color: SECTION_COLOR, fontWeight: 600 }}>Nový přes ARES</button>.
                            </div>
                        </div>
                    )}

                    {/* IČO input + lookup (nový přes ARES) */}
                    {rezim === 'ares' && (
                    <div>
                        <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                            IČO (8 číslic)
                        </label>
                        <div className="flex gap-2">
                            <input
                                value={ico}
                                onChange={e => setIco(e.target.value.replace(/\D/g, '').slice(0, 8))}
                                placeholder="12345678"
                                className="flex-1 rounded-lg px-3 py-2 text-sm font-mono"
                                style={{ ...cardStyle, color: 'var(--text-primary)' }}
                                onKeyDown={e => e.key === 'Enter' && handleAresLookup()}
                            />
                            <button
                                onClick={handleAresLookup}
                                disabled={ico.length !== 8 || hledani}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
                                style={{ background: SECTION_COLOR, opacity: ico.length !== 8 || hledani ? 0.5 : 1 }}
                            >
                                {hledani ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                                Dohledat v ARES
                            </button>
                        </div>
                    </div>
                    )}

                    {/* Ruční / zahraniční dodavatel (ARES nedohledá) */}
                    {rezim === 'rucni' && (
                        <div className="space-y-3">
                            <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                                Pro zahraniční dodavatele (Anthropic ap.) a doklady bez IČO. DIČ může být i zahraniční VAT. Po spárování se název zapamatuje → příští doklad se spáruje sám.
                            </div>
                            {[
                                ['nazev', 'Název *', 'Anthropic PBC'],
                                ['dic', 'DIČ / VAT', 'US / EU VAT'],
                                ['adresa', 'Adresa', ''],
                                ['iban', 'IBAN', ''],
                                ['cislo_uctu', 'Číslo účtu', ''],
                            ].map(([k, label, ph]) => (
                                <div key={k}>
                                    <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>{label}</label>
                                    <input value={form[k]} onChange={e => setF(k, e.target.value)} placeholder={ph}
                                        className="w-full rounded-lg px-3 py-2 text-sm" style={{ ...cardStyle, color: 'var(--text-primary)' }} />
                                </div>
                            ))}
                            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                <input type="checkbox" checked={form.je_platce_dph} onChange={e => setF('je_platce_dph', e.target.checked)} />
                                Plátce DPH
                            </label>
                            {/* Kategorie */}
                            <div>
                                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Kategorie dodavatele</label>
                                <div className="flex flex-wrap gap-2">
                                    {kategorieDostupne.map(k => {
                                        const Icon = IKONA_KATEGORIE[k.ikona] || Building2;
                                        const selected = selectedKategorie.includes(k.kod);
                                        return (
                                            <button key={k.id} onClick={() => toggleKategorie(k.kod)}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium"
                                                style={{ background: selected ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-raised)'), color: selected ? 'white' : 'var(--text-secondary)', border: selected ? 'none' : '1px solid var(--border-default)' }}>
                                                <Icon size={12} />{k.nazev}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Chyba */}
                    {chyba && (
                        <div className="p-3 rounded-lg flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                            <AlertCircle size={16} style={{ color: '#ef4444', marginTop: 1 }} />
                            <div className="text-sm" style={{ color: '#ef4444' }}>{chyba}</div>
                        </div>
                    )}

                    {/* ARES preview */}
                    {aresPreview && (
                        <>
                            <div className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                                            {aresPreview.nazev}
                                        </div>
                                        <div className="text-xs font-mono mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                                            IČO {aresPreview.ico}
                                            {aresPreview.dic && ` · DIČ ${aresPreview.dic}`}
                                            {aresPreview.je_platce_dph && (
                                                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>
                                                    plátce DPH
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <Check size={20} style={{ color: '#10b981' }} />
                                </div>
                                {aresPreview.adresa && (
                                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                        📍 {aresPreview.adresa}
                                    </div>
                                )}
                                {aresPreview.pravni_forma && (
                                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                        {aresPreview.pravni_forma}
                                    </div>
                                )}
                                {aresPreview.okec_kody?.length > 0 && (
                                    <div className="text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                                        OKEČ: {aresPreview.okec_kody.slice(0, 5).join(', ')}
                                        {aresPreview.okec_kody.length > 5 && `… +${aresPreview.okec_kody.length - 5}`}
                                    </div>
                                )}
                            </div>

                            {/* Kategorie selector */}
                            <div>
                                <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>
                                    Kategorie dodavatele
                                    {aresPreview.navrhovane_kategorie?.length > 0 && (
                                        <span className="ml-1 font-normal" style={{ color: 'var(--text-tertiary)' }}>
                                            (návrh z OKEČ: {aresPreview.navrhovane_kategorie.join(', ')})
                                        </span>
                                    )}
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {kategorieDostupne.map(k => {
                                        const Icon = IKONA_KATEGORIE[k.ikona] || Building2;
                                        const selected = selectedKategorie.includes(k.kod);
                                        return (
                                            <button
                                                key={k.id}
                                                onClick={() => toggleKategorie(k.kod)}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                                style={{
                                                    background: selected ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-raised)'),
                                                    color: selected ? 'white' : 'var(--text-secondary)',
                                                    border: selected ? 'none' : '1px solid var(--border-default)',
                                                }}
                                            >
                                                <Icon size={12} />
                                                {k.nazev}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 p-4" style={{ borderTop: '1px solid var(--border-default)' }}>
                    <button
                        onClick={onClose}
                        className="px-3 py-2 rounded-lg text-sm font-medium"
                        style={{ color: 'var(--text-secondary)' }}
                    >
                        Zrušit
                    </button>
                    {rezim === 'ares' && (
                        <button
                            onClick={handleZalozit}
                            disabled={!aresPreview || zakladani}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
                            style={{ background: SECTION_COLOR, opacity: !aresPreview || zakladani ? 0.5 : 1 }}
                        >
                            {zakladani && <Loader2 size={14} className="animate-spin" />}
                            {dokladId ? 'Založit a spárovat s dokladem' : 'Založit dodavatele'}
                        </button>
                    )}
                    {rezim === 'rucni' && (
                        <button
                            onClick={handleZalozitRucni}
                            disabled={!form.nazev.trim() || zakladani}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
                            style={{ background: SECTION_COLOR, opacity: !form.nazev.trim() || zakladani ? 0.5 : 1 }}
                        >
                            {zakladani && <Loader2 size={14} className="animate-spin" />}
                            {dokladId ? 'Založit a spárovat s dokladem' : 'Založit dodavatele'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
